import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  People as PeopleIcon,
  EggAlt as EggIcon,
  Group as GroupIcon,
  Pending as PendingIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { supabase } from '../services/supabase';
import { format, subDays, startOfDay } from 'date-fns';

function StatCard({ title, value, icon, color }) {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="text.secondary" gutterBottom variant="overline">
              {title}
            </Typography>
            <Typography variant="h4">{value}</Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: color + '20',
              borderRadius: '50%',
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {React.cloneElement(icon, { sx: { color, fontSize: 32 } })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    todayUsers: 0,
    todayShells: 0,
    totalUsers: 0,
    pendingWithdrawals: 0,
    todayAdViews: 0,
  });
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const todayStart = startOfDay(new Date()).toISOString();
      const sevenDaysAgo = startOfDay(subDays(new Date(), 6)).toISOString();

      // 오늘 가입자 수
      const { count: todayUsers } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', today);

      // 오늘 지급된 등껍질 수
      const { count: todayShells } = await supabase
        .from('eggs')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', today);

      // 총 회원수
      const { count: totalUsers } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true });

      // 출금 대기 건수
      const { count: pendingWithdrawals } = await supabase
        .from('withdrawal_requests')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      // 오늘 광고 노출 수 (opened eggs today)
      const { count: todayAdViews } = await supabase
        .from('eggs')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'opened')
        .gte('opened_at', todayStart);

      // 최근 7일 등껍질 오픈 내역
      const { data: recentEggs } = await supabase
        .from('eggs')
        .select('opened_at')
        .eq('status', 'opened')
        .gte('opened_at', sevenDaysAgo);

      // 최근 7일 신규 가입자 내역
      const { data: recentUsers } = await supabase
        .from('users')
        .select('created_at')
        .gte('created_at', sevenDaysAgo);

      // 일별 데이터 집계
      const dailyData = buildChartData(recentEggs || [], recentUsers || []);

      setStats({
        todayUsers: todayUsers || 0,
        todayShells: todayShells || 0,
        totalUsers: totalUsers || 0,
        pendingWithdrawals: pendingWithdrawals || 0,
        todayAdViews: todayAdViews || 0,
      });
      setChartData(dailyData);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const buildChartData = (eggs, users) => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const date = subDays(new Date(), i);
      const dateStr = format(date, 'yyyy-MM-dd');
      days.push({
        date: format(date, 'MM/dd'),
        dateKey: dateStr,
        광고노출: 0,
        신규가입: 0,
      });
    }

    const dayMap = new Map(days.map((d) => [d.dateKey, d]));

    for (const egg of eggs) {
      if (!egg.opened_at) continue;
      const key = format(new Date(egg.opened_at), 'yyyy-MM-dd');
      const entry = dayMap.get(key);
      if (entry) entry.광고노출 += 1;
    }

    for (const user of users) {
      if (!user.created_at) continue;
      const key = format(new Date(user.created_at), 'yyyy-MM-dd');
      const entry = dayMap.get(key);
      if (entry) entry.신규가입 += 1;
    }

    return days.map(({ dateKey, ...rest }) => rest);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        대시보드
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        오늘 날짜: {format(new Date(), 'yyyy년 MM월 dd일')}
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="오늘 가입자"
            value={stats.todayUsers}
            icon={<PeopleIcon />}
            color="#2196f3"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="오늘 등껍질 지급"
            value={stats.todayShells}
            icon={<EggIcon />}
            color="#ff9800"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="오늘 광고 노출"
            value={stats.todayAdViews}
            icon={<VisibilityIcon />}
            color="#2D9500"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="총 회원수"
            value={stats.totalUsers.toLocaleString()}
            icon={<GroupIcon />}
            color="#4caf50"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="출금 대기"
            value={stats.pendingWithdrawals}
            icon={<PendingIcon />}
            color="#f44336"
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              최근 7일 일별 통계
            </Typography>
            <Box sx={{ width: '100%', height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="광고노출" fill="#2D9500" />
                  <Bar dataKey="신규가입" fill="#2196f3" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default Dashboard;
